<template>
  <ModalRoot #body="{ close }" v-model="appStore.showCookieSettings">
    <ModalBodyV2
      :hide-actions="true"
      class="!p-0 !bg-brand-light-bg-overlay-50 !rounded-[40px] overflow-hidden"
      @close="close"
    >
      <div
        class="flex p-6 flex-col justify-center items-start gap-6 shrink-0 rounded-[35px] bg-fill-brand-secondary backdrop-blur-[20px]"
      >
        <!-- Header -->
        <div class="flex justify-between items-center self-stretch">
          <span class="text-text font-sans text-lg font-bold">
            {{ $t("cookieSettings.title") }}
          </span>

          <ButtonV2
            variant="subtle"
            size="md"
            type="submit"
            aria-label="Close"
            @click="appStore.showCookieSettings = false"
          >
            <Icon
              icon="mdi:close"
              class="w-6 h-6 text-lg text-basics-primary"
              aria-hidden
            />
          </ButtonV2>
        </div>

        <form
          class="flex-col justify-start items-start gap-4 inline-flex font-sans"
          @submit.prevent="saveCookieConsent"
        >
          <div class="self-stretch text-text text-base font-medium">
            <span>{{ $t("cookieSettings.description") }} </span>
            <button class="underline" @click="navigateToPrivacyPolicy">
              {{ $t("cookieSettings.privacyPolicy") }}
            </button>
            <span>.</span>
          </div>
          <div class="self-stretch justify-between items-center inline-flex">
            <label
              for="cookie-marketing"
              class="text-text text-base font-medium"
            >
              {{ $t("cookieSettings.marketing") }}
            </label>
            <Toggle
              id="cookie-marketing"
              v-model="localCookieConsent.marketing"
            />
          </div>
          <div class="self-stretch justify-between items-center inline-flex">
            <label
              for="cookie-personalization"
              class="text-text text-base font-medium"
            >
              {{ $t("cookieSettings.personalization") }}
            </label>
            <Toggle
              id="cookie-personalization"
              v-model="localCookieConsent.personalization"
            />
          </div>
          <div class="self-stretch justify-between items-center inline-flex">
            <label
              for="cookie-analytics"
              class="text-text text-base font-medium"
            >
              {{ $t("cookieSettings.analytics") }}
            </label>
            <Toggle
              id="cookie-analytics"
              v-model="localCookieConsent.analytics"
            />
          </div>
        </form>
        <ButtonV2
          variant="primary"
          size="md"
          type="submit"
          aria-label="Save"
          class="self-stretch h-10"
          @click="saveCookieConsent"
        >
          {{ $t("cookieSettings.savePreferences") }}
        </ButtonV2>
      </div>
    </ModalBodyV2>
  </ModalRoot>
</template>

<script setup lang="ts">
import { useAppStore } from "@/stores/app.store";
import { createDenyAllConsent, type CookieConsent } from "@/utils/cookies";
import { ref, watchEffect } from "vue";
import ButtonV2 from "../ui/button/button-v2.vue";
import { Icon } from "@iconify/vue/dist/iconify.js";
import ModalRoot from "../ui/modal/modal-root.vue";
import ModalBodyV2 from "../ui/modal/modal-body-v2.vue";
import { useRouter } from "vue-router";
import { RouteNames } from "@/router/app.router";
import Toggle from "./toggle.vue";
import { useMutation } from "@urql/vue";
import { graphql as gql } from "@/generated";

const appStore = useAppStore();

const localCookieConsent = ref<CookieConsent>(createDenyAllConsent());

watchEffect(() => {
  localCookieConsent.value = appStore.cookieConsent ?? createDenyAllConsent();
});

const LogCookieConsentDocument = gql(/* GraphQL */ `
  mutation LogCookieConsent(
    $marketing: Boolean!
    $personalization: Boolean!
    $analytics: Boolean!
  ) {
    logCookieConsent(
      marketing: $marketing
      personalization: $personalization
      analytics: $analytics
    )
  }
`);

const { executeMutation: executeLogCookieConsent } = useMutation(
  LogCookieConsentDocument,
);

function saveCookieConsent() {
  const consent: CookieConsent = {
    version: 2,
    marketing: localCookieConsent.value.marketing,
    personalization: localCookieConsent.value.personalization,
    analytics: localCookieConsent.value.analytics,
    consented_at: Date.now(),
  };

  appStore.cookieConsent = consent;
  appStore.showCookieSettings = false;

  executeLogCookieConsent({
    marketing: consent.marketing,
    personalization: consent.personalization,
    analytics: consent.analytics,
  });
}

const router = useRouter();
function navigateToPrivacyPolicy() {
  appStore.showCookieSettings = false;
  router.push({ name: RouteNames.PrivacyPolicy });
}
</script>
